import { t } from '@lingui/core/macro';
import {
  FormatBoldOutlined,
  FormatItalicOutlined,
  FormatListBulletedOutlined,
  FormatListNumberedOutlined,
  LinkOutlined,
  Looks3Outlined,
  LooksTwoOutlined,
  StrikethroughSOutlined,
} from '@mui/icons-material';
import { Divider, Stack } from '@mui/material';
import type { Editor } from '@tiptap/react';
import { type FC, useState } from 'react';
import { EmojiPickerButton } from './EmojiPickerButton';
import { LinkDialog } from './LinkDialog';
import { ToolbarButton } from './ToolbarButton';

type Props = {
  editor: Editor;
};

type LinkDialogState = {
  initialText: string;
  initialUrl: string;
  range: { from: number; to: number };
};

export const EditorToolbar: FC<Props> = ({ editor }) => {
  const [linkDialog, setLinkDialog] = useState<LinkDialogState | null>(null);

  const openLinkDialog = () => {
    if (editor.isActive('link')) {
      editor.chain().extendMarkRange('link').run();
    }
    const { from, to } = editor.state.selection;
    const initialText = editor.state.doc.textBetween(from, to, ' ');
    const initialUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
    setLinkDialog({ initialText, initialUrl, range: { from, to } });
  };

  const handleSubmitLink = (text: string, url: string) => {
    if (!linkDialog) return;
    const { range } = linkDialog;
    editor
      .chain()
      .focus()
      .setTextSelection(range)
      .insertContent(text)
      .setTextSelection({ from: range.from, to: range.from + text.length })
      .setLink({ href: url })
      .run();
    setLinkDialog(null);
  };

  const handleRemoveLink = () => {
    if (!linkDialog) return;
    editor.chain().focus().setTextSelection(linkDialog.range).unsetLink().run();
    setLinkDialog(null);
  };

  return (
    <>
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mb: 1 }}>
        <ToolbarButton
          label={t`Gras`}
          icon={<FormatBoldOutlined fontSize="small" />}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          label={t`Italique`}
          icon={<FormatItalicOutlined fontSize="small" />}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          label={t`Barré`}
          icon={<StrikethroughSOutlined fontSize="small" />}
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <Divider orientation="vertical" sx={{ mx: 0.5 }} />
        <ToolbarButton
          label={t`Titre H2`}
          icon={<LooksTwoOutlined fontSize="small" />}
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <ToolbarButton
          label={t`Titre H3`}
          icon={<Looks3Outlined fontSize="small" />}
          isActive={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        />
        <Divider orientation="vertical" sx={{ mx: 0.5 }} />
        <ToolbarButton
          label={t`Liste à puces`}
          icon={<FormatListBulletedOutlined fontSize="small" />}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolbarButton
          label={t`Liste numérotée`}
          icon={<FormatListNumberedOutlined fontSize="small" />}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <ToolbarButton
          label={editor.isActive('link') ? t`Modifier le lien` : t`Insérer un lien`}
          icon={<LinkOutlined fontSize="small" />}
          isActive={editor.isActive('link')}
          onClick={openLinkDialog}
        />
        <EmojiPickerButton editor={editor} />
      </Stack>
      {linkDialog && (
        <LinkDialog
          initialText={linkDialog.initialText}
          initialUrl={linkDialog.initialUrl}
          isEditing={linkDialog.initialUrl.length > 0}
          onClose={() => setLinkDialog(null)}
          onSubmit={handleSubmitLink}
          onRemove={handleRemoveLink}
        />
      )}
    </>
  );
};
